import { useEffect, useState } from "react";

import { img, LOGO_W, LOGO_H } from "@/assets/images";

const LOGO_SRC = img.logo;
const MIN_MS = 700;
const MAX_MS = 1400;

interface Props {
  onDone?: () => void;
}

const PremiumLoader = ({ onDone }: Props) => {
  const [hiding, setHiding] = useState(false);
  const [removed, setRemoved] = useState(false);

  useEffect(() => {
    const start = performance.now();
    let hideTimer: ReturnType<typeof setTimeout>;
    let removeTimer: ReturnType<typeof setTimeout>;

    const hide = () => {
      const elapsed = performance.now() - start;
      const wait = Math.max(MIN_MS - elapsed, 0);
      hideTimer = setTimeout(() => {
        setHiding(true);
        removeTimer = setTimeout(() => {
          setRemoved(true);
          onDone?.();
        }, 550);
      }, wait);
    };

    const maxTimer = setTimeout(hide, MAX_MS);
    if (document.readyState === "complete") {
      hide();
    } else {
      window.addEventListener("load", hide, { once: true });
    }

    return () => {
      clearTimeout(hideTimer);
      clearTimeout(removeTimer);
      clearTimeout(maxTimer);
      window.removeEventListener("load", hide);
    };
  }, [onDone]);

  if (removed) return null;

  return (
    <div
      aria-hidden={hiding}
      role="status"
      aria-live="polite"
      className={`fixed inset-0 z-[10000] flex items-center justify-center overflow-hidden transition-all duration-[550ms] ease-[cubic-bezier(0.65,0,0.35,1)] ${
        hiding ? "opacity-0 pointer-events-none scale-[1.04]" : "opacity-100 scale-100"
      }`}
      style={{
        background:
          "radial-gradient(130% 100% at 50% -10%, hsl(0 72% 12%) 0%, hsl(0 0% 7%) 45%, hsl(0 0% 4%) 100%)",
      }}
    >
      {/* Ambient brand glows */}
      <div
        aria-hidden="true"
        className="absolute -top-32 left-1/2 -translate-x-1/2 h-[30rem] w-[30rem] rounded-full blur-3xl opacity-50 motion-safe:animate-pulse"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.35), transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-40 -right-28 h-[26rem] w-[26rem] rounded-full blur-3xl opacity-30"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.25), transparent 70%)" }}
      />
      {/* Faint grid texture */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(hsl(0 0% 100% / 1) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100% / 1) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse at center, black 20%, transparent 70%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 20%, transparent 70%)",
        }}
      />

      <div className="relative flex flex-col items-center px-6">
        {/* Logo inside dual rotating brand rings */}
        <div className="relative mb-9 flex items-center justify-center">
          {/* Outer sweeping ring */}
          <div
            aria-hidden="true"
            className="absolute -inset-7 rounded-full opacity-90 motion-safe:animate-spin"
            style={{
              background:
                "conic-gradient(from 0deg, transparent 0deg, hsl(var(--primary)) 70deg, transparent 160deg, transparent 360deg)",
              WebkitMask:
                "radial-gradient(farthest-side, transparent calc(100% - 2.5px), #000 calc(100% - 2.5px))",
              mask: "radial-gradient(farthest-side, transparent calc(100% - 2.5px), #000 calc(100% - 2.5px))",
              animationDuration: "1.5s",
            }}
          />
          {/* Inner counter-rotating ring */}
          <div
            aria-hidden="true"
            className="absolute -inset-3.5 rounded-full opacity-70 motion-safe:animate-spin"
            style={{
              background:
                "conic-gradient(from 180deg, transparent 0deg, hsl(0 0% 100% / 0.5) 50deg, transparent 130deg, transparent 360deg)",
              WebkitMask:
                "radial-gradient(farthest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1.5px))",
              mask: "radial-gradient(farthest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1.5px))",
              animationDuration: "2.4s",
              animationDirection: "reverse",
            }}
          />
          {/* Logo card */}
          <div className="relative rounded-[1.5rem] bg-white px-8 py-6 shadow-[0_24px_70px_-20px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.08)] motion-safe:animate-[loader-logo-in_0.6s_cubic-bezier(0.33,1,0.68,1)]">
            <img
              src={LOGO_SRC}
              alt="Good Choice Cleaning & Multi Services"
              className="relative h-14 w-auto md:h-16"
              width={LOGO_W}
              height={LOGO_H}
              fetchPriority="high"
            />
          </div>
        </div>

        {/* Wordmark */}
        <p className="relative mb-5 font-heading text-sm md:text-base font-bold uppercase tracking-[0.28em] text-white/90 motion-safe:animate-[loader-fade-up_0.7s_0.1s_both]">
          Good Choice Cleaning
        </p>

        {/* Indeterminate brand progress bar */}
        <div className="relative h-[3px] w-56 overflow-hidden rounded-full bg-white/10">
          <div
            className="absolute inset-y-0 left-0 w-1/3 rounded-full"
            style={{
              background:
                "linear-gradient(90deg, transparent, hsl(var(--primary)), hsl(0 84% 62%), transparent)",
              animation: "premium-loader-bar 1.1s ease-in-out infinite",
            }}
          />
        </div>

        <p className="relative mt-4 text-[10px] font-semibold tracking-[0.34em] uppercase text-white/40">
          Preparing your spotless experience
        </p>
      </div>

      <style>{`
        @keyframes premium-loader-bar {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(420%); }
        }
        @keyframes loader-logo-in {
          0% { opacity: 0; transform: translateY(10px) scale(0.94); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes loader-fade-up {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [role="status"] * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  );
};

export default PremiumLoader;
